import positioning from '../../data/dotsPositioning.json';
import { useIsDark } from '../../useIsDark';

/**
 * ComparisonTable — the function comparison for blog28, Figure 2 (replaces AccountabilityMapChart).
 *
 * WHY THIS REPLACED A CHART. The first version of this figure was a two-axis scatter that had to
 * caption itself "a structural model, not a measurement". A picture that needs a paragraph to say
 * its numbers are not numbers is still read as a score, and a score nobody measured has no place in
 * a comparison. The reviewer's words were "nobody will believe this graph", and that was right.
 *
 * Chart-type-first: this content is a row-by-row comparison of published capabilities, so the
 * correct form is a table. It is also the better answer-engine asset: each row is quotable on its
 * own, which is the GEO discipline the blog rules require.
 *
 * The verdict column is included because a reader who skims only this box must still get the
 * answer (Rule 37), and it states where the other product leads rather than flattening the answer.
 * Accepts `height` because ChartZoom clones its child for the lightbox.
 */

type Row = { need: string; theirs: string; ours: string; leads: string };

export const ComparisonTable = ({ height, dark }: { height?: number; dark?: boolean }) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const fig = (
    positioning as unknown as {
      figures: {
        comparison_table: {
          source: string;
          columns: string[];
          rows: Row[];
          bottom_line: string;
        };
      };
    }
  ).figures.comparison_table;

  const C = isDark
    ? { bg: '#0F172A', head: '#0B1220', border: '#334155', text: '#E2E8F0', muted: '#94A3B8', ours: '#0AB3BF' }
    : { bg: '#FFFFFF', head: '#F8FAFC', border: '#CBD5E1', text: '#334155', muted: '#64748B', ours: '#087B8B' };

  const teal = '#0AB3BF';

  return (
    <figure className="not-prose">
      <div
        className="rounded-xl overflow-hidden"
        style={{ border: `1px solid ${C.border}`, background: C.bg, minHeight: height }}
      >
        {/* Desktop: four columns. */}
        <table className="hidden sm:table w-full text-left text-sm" style={{ color: C.text }}>
          <thead>
            <tr style={{ background: C.head }}>
              {fig.columns.map((c) => (
                <th key={c} className="px-3 py-2 font-semibold align-bottom" style={{ borderBottom: `2px solid ${teal}` }}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fig.rows.map((r) => (
              <tr key={r.need} style={{ borderTop: `1px solid ${C.border}` }}>
                <td className="px-3 py-3 align-top font-semibold">{r.need}</td>
                <td className="px-3 py-3 align-top">{r.theirs}</td>
                <td className="px-3 py-3 align-top" style={{ color: C.ours }}>
                  {r.ours}
                </td>
                <td className="px-3 py-3 align-top" style={{ color: C.muted }}>
                  {r.leads}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Phone: one card per row, never a scaled table (Rule 41). */}
        <div className="sm:hidden">
          {fig.rows.map((r) => (
            <div key={r.need} className="px-3 py-3" style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="text-[13px] font-semibold break-words">{r.need}</div>
              <div className="text-[12px] mt-2 break-words">
                <span className="font-semibold" style={{ color: C.muted }}>
                  Theirs:{' '}
                </span>
                {r.theirs}
              </div>
              <div className="text-[12px] mt-2 break-words">
                <span className="font-semibold" style={{ color: teal }}>
                  Ours:{' '}
                </span>
                <span style={{ color: C.ours }}>{r.ours}</span>
              </div>
              <div className="text-[11px] mt-2 break-words" style={{ color: C.muted }}>
                {r.leads}
              </div>
            </div>
          ))}
        </div>

        <div
          className="px-3 py-3 text-[12px] leading-relaxed break-words"
          style={{ borderTop: `2px solid ${teal}`, color: C.text, background: C.head }}
        >
          <span className="font-semibold">The short version. </span>
          {fig.bottom_line}
        </div>
      </div>
      <figcaption className={`mt-3 text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        Theirs is published information, named in the sources. Ours is a capability or a mechanism that exists in
        our own system. No row rests on a measurement either of us made, so no row claims one.
      </figcaption>
    </figure>
  );
};
